// src/pages/Historico/utils/chartDownload.js
import * as XLSX from 'xlsx';
import { GraficoTipo, getGrafico } from '../../../infra/api/GraficoApi';
import { filterDataBySensor, SPECTRAL_CHART_TYPES, detectSensorType } from './sensorDetection';

// ─── Chart types included in the export ───────────────────────────────────────

// Exportado: a tela de graficos deriva dela o conjunto visivel para quem nao e ADMIN,
// para que tela e arquivo nao possam divergir.
export const DOWNLOAD_TYPES = [
    GraficoTipo.SPECTRAL_TURBIDIMETRY,
    GraficoTipo.SPECTRAL_NEPHELOMETRY,
    // Fluorescência: incluída sempre, mas só sai no arquivo se o sensor do cliente
    // realmente produzir dados (sensores sem fluorescência retornam série vazia e
    // são descartados por hasRealData — a checagem de sensor via filterDataBySensor
    // já acontece no fetch).
    GraficoTipo.SPECTRAL_FLUORESCENCE,
    GraficoTipo.TEMPERATURA,
];

/**
 * Gráficos do arquivo deste experimento. A frequência ressonante só entra no LEITE: é o
 * sinal que o modelo de CBT lê; nas outras matrizes é telemetria de torre e só polui o
 * arquivo. Vai em primeiro, como a aba no plano LEITE.
 */
const tiposDoDownload = ({ incluirFrequencia = false } = {}) =>
    incluirFrequencia ? [GraficoTipo.FREQUENCIA, ...DOWNLOAD_TYPES] : DOWNLOAD_TYPES;

const TABS_META = {
    [GraficoTipo.SPECTRAL_TURBIDIMETRY]:  'Spectral Turbid.',
    [GraficoTipo.SPECTRAL_NEPHELOMETRY]:  'Spectral Neph.',
    [GraficoTipo.SPECTRAL_FLUORESCENCE]:  'Spectral Fluor.',
    [GraficoTipo.TEMPERATURA]:            'Temperaturas',
    [GraficoTipo.TEMPERATURA_SAMPLE]:     'Temp. Amostra',
    [GraficoTipo.TEMPERATURA_SAMPLE_CTRL]:'Amostra + Ctrl',
    [GraficoTipo.TEMPERATURA_CORE_CTRL]:  'Núcleo + Ctrl',
    [GraficoTipo.FREQUENCIA]:             'Frequências',
    [GraficoTipo.FONTE_ALIMENTACAO]:      'Fonte',
    [GraficoTipo.PASTILHA_PELTIER]:       'Peltier',
    [GraficoTipo.AGITADOR_MAGNETICO]:     'Agitador Magnético',
    [GraficoTipo.CONTROL_STATE]:          'Control State',
};

// ─── Column name mappings ──────────────────────────────────────────────────────

// APDS9960: channel → Portuguese name, by chart type
const APDS9960_NAMES = {
    [GraficoTipo.SPECTRAL_TURBIDIMETRY]: {
        red:   'Turbidimetria_Vermelha',
        green: 'Turbidimetria_Verde',
        blue:  'Turbidimetria_Azul',
        clear: 'Turbidimetria_Total',
        nir:   'Turbidimetria_de_Proximidade',
    },
    [GraficoTipo.SPECTRAL_NEPHELOMETRY]: {
        red:   'Nefelometria_Vermelha',
        green: 'Nefelometria_Verde',
        blue:  'Nefelometria_Azul',
        clear: 'Nefelometria_Total',
        nir:   'Nefelometria_de_Proximidade',
    },
    [GraficoTipo.SPECTRAL_FLUORESCENCE]: {
        red:   'Fluorescencia_Vermelha',
        green: 'Fluorescencia_Verde',
        blue:  'Fluorescencia_Azul',
        clear: 'Fluorescencia_Total',
        nir:   'Fluorescencia_de_Proximidade',
    },
};

// AS7341: channel → wavelength label
const AS7341_WAVELENGTHS = {
    f1: '410nm', f2: '440nm', f3: '470nm', f4: '510nm',
    f5: '550nm', f6: '583nm', f7: '620nm', f8: '670nm',
    clear: 'Branco', nir: 'Proximidade',
};

const CHART_TYPE_PREFIX = {
    [GraficoTipo.SPECTRAL_TURBIDIMETRY]: 'Turbidimetria',
    [GraficoTipo.SPECTRAL_NEPHELOMETRY]: 'Nefelometria',
    [GraficoTipo.SPECTRAL_FLUORESCENCE]: 'Fluorescencia',
};

// FREQUENCIA: canal da frequência ressonante → nome da coluna (canal 0 = o do modelo de CBT)
const FREQUENCIA_NAMES = {
    channel0: 'Frequencia_Canal_0',
    channel1: 'Frequencia_Canal_1',
};

// TEMPERATURA: lowercase series suffix → Portuguese name
const TEMPERATURA_NAMES = {
    sample:              'Temperatura_da_Amostra',
    core:                'Temperatura_do_Núcleo',
    ambient:             'Temperatura_Ambiente',
    heatsinkupper:       'Temperatura_do_Dissipador_Superior',
    heatsinklower:       'Temperatura_do_Dissipador_Inferior',
    magneticstirrer:     'Temperatura_do_Agitador_Magnético',
    coreheatexchanger:   'Temperatura_do_Núcleo_do_Trocador_de_Calor',
    sampleheatexchanger: 'Temperatura_da_Amostra_do_Trocador_de_Calor',
};

// Canonical column order in the merged output
const PREFERRED_COLUMN_ORDER = [
    // APDS9960 – Turbidimetry
    'Turbidimetria_Vermelha',
    'Turbidimetria_Verde',
    'Turbidimetria_Azul',
    'Turbidimetria_Total',
    'Turbidimetria_de_Proximidade',
    // AS7341 – Turbidimetry
    'Turbidimetria_410nm', 'Turbidimetria_440nm', 'Turbidimetria_470nm',
    'Turbidimetria_510nm', 'Turbidimetria_550nm', 'Turbidimetria_583nm',
    'Turbidimetria_620nm', 'Turbidimetria_670nm', 'Turbidimetria_Branco',
    // Frequência ressonante (só no arquivo do leite)
    'Frequencia_Canal_0',
    'Frequencia_Canal_1',
    // APDS9960 – Nephelometry
    'Nefelometria_Vermelha',
    'Nefelometria_Verde',
    'Nefelometria_Azul',
    'Nefelometria_Total',
    'Nefelometria_de_Proximidade',
    // AS7341 – Nephelometry
    'Nefelometria_410nm', 'Nefelometria_440nm', 'Nefelometria_470nm',
    'Nefelometria_510nm', 'Nefelometria_550nm', 'Nefelometria_583nm',
    'Nefelometria_620nm', 'Nefelometria_670nm', 'Nefelometria_Branco',
    // APDS9960 – Fluorescence
    'Fluorescencia_Vermelha',
    'Fluorescencia_Verde',
    'Fluorescencia_Azul',
    'Fluorescencia_Total',
    'Fluorescencia_de_Proximidade',
    // AS7341 – Fluorescence
    'Fluorescencia_410nm', 'Fluorescencia_440nm', 'Fluorescencia_470nm',
    'Fluorescencia_510nm', 'Fluorescencia_550nm', 'Fluorescencia_583nm',
    'Fluorescencia_620nm', 'Fluorescencia_670nm', 'Fluorescencia_Branco',
    // Temperatures (in priority order)
    'Temperatura_da_Amostra',
    'Temperatura_do_Núcleo',
    'Temperatura_Ambiente',
    'Temperatura_do_Dissipador_Superior',
    'Temperatura_do_Dissipador_Inferior',
    'Temperatura_do_Agitador_Magnético',
    'Temperatura_do_Núcleo_do_Trocador_de_Calor',
    'Temperatura_da_Amostra_do_Trocador_de_Calor',
];

// ─── Internal helpers ──────────────────────────────────────────────────────────

function formatSeriesName(rawName) {
    if (!rawName) return rawName;
    const idx = rawName.lastIndexOf('.');
    return idx >= 0 ? rawName.slice(idx + 1) : rawName;
}

function formatTimestamp(unixSeconds) {
    const d = new Date(unixSeconds * 1000);
    const p = n => String(n).padStart(2, '0');
    return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function cleanDataStructure(data) {
    if (!data) return null;
    const out = {};
    if (Array.isArray(data.labels)) out.labels = data.labels;
    if (data.series) {
        out.series = {};
        for (const [k, list] of Object.entries(data.series)) {
            out.series[k] = Array.isArray(list)
                ? list.map(s => ({ name: s.name, data: s.data || [] }))
                : list;
        }
    }
    return out;
}

/**
 * true se houver ao menos um valor real (não-nulo) nas séries. Sensores que não
 * medem uma modalidade (ex.: fluorescência) retornam séries só com nulos — nesse
 * caso o gráfico é descartado do export.
 */
function hasRealData(cleaned) {
    if (!cleaned?.series) return false;
    for (const list of Object.values(cleaned.series)) {
        if (!Array.isArray(list)) continue;
        for (const s of list) {
            if ((s.data || []).some(v => v != null && v !== '')) return true;
        }
    }
    return false;
}

function getColumnName(chartType, rawSeriesName, sensorType) {
    const channel = formatSeriesName(rawSeriesName).toLowerCase();

    if (chartType === GraficoTipo.TEMPERATURA) {
        return TEMPERATURA_NAMES[channel] ?? `Temperatura_${rawSeriesName}`;
    }

    if (chartType === GraficoTipo.FREQUENCIA) {
        return FREQUENCIA_NAMES[channel] ?? `Frequencia_${rawSeriesName}`;
    }

    if (sensorType === 'APDS9960') {
        return APDS9960_NAMES[chartType]?.[channel] ?? `${CHART_TYPE_PREFIX[chartType] ?? chartType}_${channel}`;
    }

    if (sensorType === 'AS7341') {
        const wl = AS7341_WAVELENGTHS[channel] ?? channel;
        return `${CHART_TYPE_PREFIX[chartType] ?? chartType}_${wl}`;
    }

    return `${CHART_TYPE_PREFIX[chartType] ?? chartType}_${channel}`;
}

/**
 * Builds a Map<timestamp, Record<columnName, value>> for a single chart result.
 */
function buildTimestampMap(cleanedData, chartType, sensorType) {
    const map = new Map();
    if (!cleanedData?.labels || !cleanedData?.series) return map;

    for (const seriesList of Object.values(cleanedData.series)) {
        if (!Array.isArray(seriesList)) continue;
        for (const series of seriesList) {
            const colName = getColumnName(chartType, series.name, sensorType);
            const values = series.data || [];
            cleanedData.labels.forEach((ts, i) => {
                if (!map.has(ts)) map.set(ts, {});
                map.get(ts)[colName] = values[i] != null ? values[i] : '';
            });
        }
    }

    return map;
}

function buildExperimentFilename(experimentId, experiment, extension) {
    const rawSerial = experiment?.serialNumber ?? experiment?.numeroSerie ?? experimentId;
    const serial = String(rawSerial).replace(/[^a-zA-Z0-9_-]/g, '_');

    let dateStr = '';
    const rawDate = experiment?.startDate;
    if (rawDate != null) {
        // startDate is stored as Unix seconds (number or numeric string), same
        // conversion used by ExperimentHeader: Number(startDate) * 1000
        const unixMs = Number.isFinite(Number(rawDate))
            ? Number(rawDate) * 1000
            : Date.parse(rawDate);  // fallback for ISO strings

        if (Number.isFinite(unixMs)) {
            const d = new Date(unixMs);
            const day   = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year  = d.getFullYear();
            dateStr = `${day}-${month}-${year}`;  // DD-MM-YYYY
        }
    }

    const rawProtocol = experiment?.protocolName ?? experiment?.nomeProtocolo;
    const protocol = rawProtocol
        ? String(rawProtocol).replace(/[^a-zA-Z0-9_-]/g, '_')
        : null;

    const parts = [];
    if (protocol) parts.push(protocol);
    if (serial) parts.push(serial);
    if (dateStr) parts.push(dateStr);

    return `${parts.join('_')}.${extension}`;
}

function triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 100);
}

// ─── Data fetching ─────────────────────────────────────────────────────────────

async function fetchAllChartData(experimentId, experiment, opcoes) {
    return Promise.all(
        tiposDoDownload(opcoes).map(async (typeKey) => {
            const label = TABS_META[typeKey] ?? typeKey;
            try {
                let data = await getGrafico(experimentId, typeKey);
                if (data && SPECTRAL_CHART_TYPES.has(typeKey)) {
                    data = filterDataBySensor(data, typeKey, experiment);
                }
                const cleaned = cleanDataStructure(data);
                return { key: typeKey, label, data: cleaned, hasData: hasRealData(cleaned), success: true };
            } catch (err) {
                console.warn(`[Download] Falha ao buscar ${label}:`, err);
                return { key: typeKey, label, error: err.message, success: false };
            }
        })
    );
}

// ─── Merged table builder ──────────────────────────────────────────────────────

/**
 * Merges all chart results into a single timestamp-aligned wide table.
 * Returns { headers: string[], rows: any[][] } or null if no data.
 */
function buildMergedTable(chartResults, experiment) {
    const timestampMaps = [];
    const allColumns = new Set();

    for (const r of chartResults) {
        if (!r.success || !r.data || !r.hasData) continue;
        const sensorType = SPECTRAL_CHART_TYPES.has(r.key)
            ? detectSensorType(r.key, experiment, r.data)
            : null;

        const map = buildTimestampMap(r.data, r.key, sensorType);
        timestampMaps.push(map);
        for (const vals of map.values()) {
            for (const k of Object.keys(vals)) allColumns.add(k);
        }
    }

    const allTimestamps = new Set();
    for (const m of timestampMaps) for (const ts of m.keys()) allTimestamps.add(ts);

    const sortedTs = Array.from(allTimestamps).sort((a, b) => a - b);
    if (sortedTs.length === 0) return null;

    const firstTs = sortedTs[0];

    // Put preferred columns first, then any extras in alphabetical order
    const orderedCols = [
        ...PREFERRED_COLUMN_ORDER.filter(c => allColumns.has(c)),
        ...Array.from(allColumns).filter(c => !PREFERRED_COLUMN_ORDER.includes(c)).sort(),
    ];

    const headers = ['Data', 'Intervalo_de_Tempo', ...orderedCols];
    const rows = sortedTs.map(ts => {
        const elapsed = parseFloat(((ts - firstTs) / 60).toFixed(2));
        const merged = {};
        for (const m of timestampMaps) Object.assign(merged, m.get(ts) ?? {});
        return [
            formatTimestamp(ts),
            elapsed,
            ...orderedCols.map(col => (merged[col] !== undefined ? merged[col] : '')),
        ];
    });

    return { headers, rows };
}

// ─── Legacy wide-table builder (used for XLSX individual sheets) ───────────────

function buildWideTable(cleaned) {
    if (!cleaned) return null;
    const labels = cleaned.labels ?? [];
    if (labels.length === 0) return null;

    const colNames = [];
    const arrays = [];
    for (const seriesList of Object.values(cleaned.series ?? {})) {
        if (!Array.isArray(seriesList)) continue;
        for (const s of seriesList) {
            colNames.push(formatSeriesName(s.name));
            arrays.push(s.data ?? []);
        }
    }

    const firstTs = labels[0];
    const headers = ['Data/Hora', 'Tempo (min)', ...colNames];
    const rows = labels.map((ts, i) => [
        formatTimestamp(ts),
        parseFloat(((ts - firstTs) / 60).toFixed(2)),
        ...arrays.map(arr => (arr[i] != null ? arr[i] : '')),
    ]);

    return { headers, rows };
}

// ─── Public exports ────────────────────────────────────────────────────────────

/**
 * Downloads the full dataset as JSON.
 */
export async function downloadAllCharts(experimentId, experiment, opcoes) {
    const results = await fetchAllChartData(experimentId, experiment, opcoes);
    const out = {
        metadata: {
            experimentId,
            serialNumber: experiment?.serialNumber ?? null,
            startDate:    experiment?.startDate    ?? null,
            type:         'FULL_DATASET',
            downloadDate: new Date().toISOString(),
        },
        charts: {},
        errors: [],
    };

    for (const r of results) {
        if (r.success && r.hasData) out.charts[r.key] = r.data;
        else if (!r.success) out.errors.push({ chart: r.label, key: r.key, message: r.error });
    }

    triggerDownload(
        new Blob([JSON.stringify(out, null, 2)], { type: 'application/json;charset=utf-8' }),
        buildExperimentFilename(experimentId, experiment, 'json'),
    );
}

/**
 * Downloads a single merged CSV with all spectral channels and temperatures.
 *
 * Columns are in Portuguese and follow the order:
 *   Data | Intervalo_de_Tempo | Turbidimetria (R/G/B/T/P) |
 *   Nefelometria (R/G/B/T/P) | Temperaturas (8 sensors)
 *
 * Filename: BioAZ_<serialNumber>_<YYYYMMDD>.csv
 */
export async function downloadAllChartsCsv(experimentId, experiment, opcoes) {
    const results = await fetchAllChartData(experimentId, experiment, opcoes);
    const table   = buildMergedTable(results, experiment);

    if (!table) {
        console.warn('[Download CSV] Nenhum dado disponível para exportar.');
        return;
    }

    const lines = [table.headers.join(';')];
    for (const row of table.rows) {
        lines.push(
            row.map(c => {
                const s = String(c);
                return s.includes(';') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
            }).join(';')
        );
    }

    // UTF-8 BOM ensures Excel opens accented characters correctly
    triggerDownload(
        new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' }),
        buildExperimentFilename(experimentId, experiment, 'csv'),
    );
}

/**
 * Downloads an Excel workbook (.xlsx):
 *   - Sheet "Dados Completos": merged table with Portuguese column names
 *   - Additional sheets: one per chart type (raw series names)
 *
 * Filename: BioAZ_<serialNumber>_<YYYYMMDD>.xlsx
 */
export async function downloadAllChartsXlsx(experimentId, experiment, opcoes) {
    const results = await fetchAllChartData(experimentId, experiment, opcoes);
    const wb = XLSX.utils.book_new();

    // First sheet: merged / complete data
    const merged = buildMergedTable(results, experiment);
    if (merged) {
        const ws = XLSX.utils.aoa_to_sheet([merged.headers, ...merged.rows]);
        ws['!cols'] = merged.headers.map(h => ({ wch: Math.max(h.length + 2, 14) }));
        XLSX.utils.book_append_sheet(wb, ws, 'Dados Completos');
    }

    // Individual sheets per chart type
    for (const r of results) {
        if (!r.success || !r.hasData) continue;
        const table = buildWideTable(r.data);
        if (!table) continue;
        const ws = XLSX.utils.aoa_to_sheet([table.headers, ...table.rows]);
        ws['!cols'] = table.headers.map(h => ({ wch: Math.max(h.length + 2, 12) }));
        XLSX.utils.book_append_sheet(wb, ws, (r.label ?? r.key).slice(0, 31));
    }

    XLSX.writeFile(wb, buildExperimentFilename(experimentId, experiment, 'xlsx'));
}
